import {
  CalendarEventAvailability,
  CalendarEventDetails,
  CalendarEventFormFields,
  CalendarEventGuest,
  CalendarEventResponseStatus,
  CalendarRef,
  Timeslot,
} from '@griply/types';
import { addDays, addMilliseconds, startOfDay, subDays } from 'date-fns';

import { parseDateKey } from './format-date-key';
import { parseNaiveCalendarDateTime } from './format-naive-calendar-date-time';
import { getCalendarEventTimeZoneOverride } from './get-calendar-event-time-zone-override';
import { getCalendarTaskStartTime } from './get-calendar-task-dates';
import { getExternalEventShowAs } from './get-external-event-show-as';
import { htmlToPlainText } from './html-to-plain-text';

export const DEFAULT_CALENDAR_EVENT_DURATION_MS = 60 * 60 * 1000;

// Everything a brand-new / not-yet-fetched event form starts as. Both builders below spread
// it so a field added to CalendarEventFormFields cannot be wired into only one of them —
// that drift already happened once, when `timeZone` landed in two places.
const emptyEventFormFields = (calendar: CalendarRef | null) => ({
  description: '',
  timeZone: null,
  rrule: null,
  guests: [],
  videoCall: false,
  replaceVideoCall: false,
  location: '',
  calendar,
  availability: CalendarEventAvailability.Busy,
  reminders: null,
});

export type CreateCalendarEventInitial = {
  date: Date;
  timeslot: Timeslot | null;
};

export const buildCreateEventFormFields = (
  { date, timeslot }: CreateCalendarEventInitial,
  calendar: CalendarRef | null,
): CalendarEventFormFields => {
  const day = startOfDay(date);
  const startTime = timeslot?.startTime ?? null;
  const timedSlot = startTime !== null;

  return {
    title: '',
    allDay: !timedSlot,
    startDate: day,
    startTime,
    endDate: day,
    endTime: timedSlot
      ? startTime + (timeslot?.duration ?? DEFAULT_CALENDAR_EVENT_DURATION_MS)
      : null,
    ...emptyEventFormFields(calendar),
  };
};

export type CalendarEventSeedInput = {
  title: string;
  allDay: boolean;
  start: Date;
  // absent on a zero-length grid event; falls back to a one-hour block
  end: Date | null;
  calendar: CalendarRef | null;
};

// The instantly-available half of the form: everything a rendered grid event already knows.
// Every remaining field stays at its empty value so the popup can skeleton those rows until
// the detail fetch resolves.
export const buildEventFormSeed = ({
  title,
  allDay,
  start,
  end,
  calendar,
}: CalendarEventSeedInput): CalendarEventFormFields => {
  // A missing end collapses to a single day when all-day (matching the drag patch's
  // mapEventTimesToUpdateInput) rather than inverting the span; a timed one gets the
  // default block.
  const resolvedEnd =
    end ??
    (allDay
      ? addDays(start, 1)
      : addMilliseconds(start, DEFAULT_CALENDAR_EVENT_DURATION_MS));

  return {
    title,
    allDay,
    startDate: startOfDay(start),
    startTime: allDay ? null : getCalendarTaskStartTime(start),
    // a grid all-day end is exclusive; the form (like the provider's detail payload) is inclusive
    endDate: allDay
      ? startOfDay(subDays(resolvedEnd, 1))
      : startOfDay(resolvedEnd),
    endTime: allDay ? null : getCalendarTaskStartTime(resolvedEnd),
    ...emptyEventFormFields(calendar),
  };
};

const KNOWN_RESPONSE_STATUSES = Object.values(CalendarEventResponseStatus);

// a status this build doesn't know (newer backend) reads as "no answer yet", not as a crash or a lie
const coerceGuestResponseStatus = (
  guest: CalendarEventGuest,
): CalendarEventGuest => {
  if (guest.responseStatus === undefined) {
    return guest;
  }
  if (KNOWN_RESPONSE_STATUSES.includes(guest.responseStatus)) {
    return guest;
  }
  return { ...guest, responseStatus: CalendarEventResponseStatus.NeedsAction };
};

export const mapEventDetailsToFormState = (
  details: CalendarEventDetails,
  calendarTimeZone?: string | null,
): CalendarEventFormFields => {
  const start = details.allDay
    ? { date: parseDateKey(details.start), timeOfDayMs: null }
    : parseNaiveCalendarDateTime(details.start);
  const end = details.allDay
    ? { date: parseDateKey(details.end), timeOfDayMs: null }
    : parseNaiveCalendarDateTime(details.end);

  return {
    title: details.title,
    description: htmlToPlainText(details.description ?? ''),
    allDay: details.allDay,
    startDate: start.date,
    startTime: start.timeOfDayMs,
    endDate: end.date,
    endTime: end.timeOfDayMs,
    timeZone: getCalendarEventTimeZoneOverride(
      details.timeZone,
      calendarTimeZone,
    ),
    rrule: details.rrule,
    guests: details.guests.map(coerceGuestResponseStatus),
    videoCall: details.videoCall,
    // Never seeded from the event: replacing is an intent the user forms inside this editing session,
    // not a property of what was loaded. Re-opening an event must not re-arm the swap.
    replaceVideoCall: false,
    location: details.location ?? '',
    calendar: {
      connectionId: details.connectionId,
      calendarId: details.calendarId,
    },
    // showAs when the backend sends it; otherwise the boolean is all there is to go on
    availability: getExternalEventShowAs(
      details.showAs ??
        (details.busy
          ? CalendarEventAvailability.Busy
          : CalendarEventAvailability.Free),
    ),
    reminders: details.usesDefaultReminders ? null : details.reminders,
  };
};
